/**
 * svgPipeline — standard one-time SVG modifiers applied once before cache.
 *
 * Pipeline order (orchestrated by useProductSvg):
 *   1. decodeSvgBuffer   — raw bytes → UTF-8 string      (shared/utils/svgUtils.js)
 *   2. DOMParser         — string → SVGElement           (caller's responsibility)
 *   3. applyStandardSvgModifiers — all one-time DOM mutations (this file)
 *
 * Mode/variant-specific mutations:
 *   - 3D (render_top) → svgRenderTop.js
 *   - Color scheme    → svgColorScheme.js
 */

// ── Standard one-time modifiers ───────────────────────────────────────────────

/**
 * Rewrites every <use href="..."> to an absolute defs.svg URL.
 * In inline SVG, relative hrefs pointing to an external file are not resolved
 * by the browser — we must make them absolute before injection.
 *
 * Exported as a named helper so other pipelines (e.g. `useCompartmentVariants`)
 * can reuse it on per-variant SVG fragments without pulling in the full
 * `applyStandardSvgModifiers` pass.
 *
 * @param {SVGElement} svgElement
 * @param {string} defsBaseUrl — absolute URL of defs.svg (without #fragment)
 */
export function rewriteUseHrefs(svgElement, defsBaseUrl) {
  const uses = svgElement.querySelectorAll('use')
  for (const use of uses) {
    const href = use.getAttribute('href') ?? use.getAttributeNS('http://www.w3.org/1999/xlink', 'href')
    if (!href) continue
    const fragment = href.includes('#') ? href.split('#')[1] : href
    use.setAttribute('href', `${defsBaseUrl}#${fragment}`)
  }
}

/**
 * Rewrites marker-end / marker-start URL references on stairway paths
 * to absolute defs.svg URLs.
 *
 * @param {SVGElement} svgElement
 * @param {string} defsBaseUrl
 */
function _rewriteMarkerHrefs(svgElement, defsBaseUrl) {
  const stairways = svgElement.querySelector('.stairways')
  if (!stairways) return
  const paths = stairways.querySelectorAll('path[marker-end], path[marker-start]')
  for (const path of paths) {
    for (const attr of ['marker-end', 'marker-start']) {
      const val = path.getAttribute(attr)
      if (!val) continue
      // Extract the id from "url(…#id)" or "url(#id)"
      const match = val.match(/url\(([^)]+)\)/)
      if (!match) continue
      const fragment = match[1].includes('#') ? match[1].split('#')[1] : match[1].replace(/^#/, '')
      path.setAttribute(attr, `url(${defsBaseUrl}#${fragment})`)
    }
  }
}

/**
 * Re-orders compartment <g number="N"> elements inside .compartments to be
 * sorted descending by number — ensures correct z-order stacking in SVG.
 *
 * @param {SVGElement} svgElement
 */
function _reorderCompartmentsGroup(svgElement) {
  const compartmentsGroup = svgElement.querySelector('.compartments')
  if (!compartmentsGroup) return
  const groups = Array.from(compartmentsGroup.querySelectorAll('g[number]'))
  groups.sort((a, b) => Number(b.getAttribute('number')) - Number(a.getAttribute('number')))
  for (const g of groups) {
    compartmentsGroup.appendChild(g)
  }
}

/**
 * Injects a <pattern> definition for the 3D texture into the SVG <defs>.
 * The pattern references a PNG file at the same path as the SVG.
 *
 * The pattern is always added to the cached element so that the 3D renderer
 * can immediately apply `fill="url(#treeDStyle-default)"` without a second
 * DOM modification pass. The PNG is not fetched until a rect referencing the
 * pattern appears in the rendered DOM.
 *
 * @param {SVGElement} svgElement
 * @param {string} pngUrl — absolute URL of the floor plan PNG texture
 */
function _injectThreeDPatternDef(svgElement, pngUrl) {
  const svgNS = 'http://www.w3.org/2000/svg'
  let defs = svgElement.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS(svgNS, 'defs')
    svgElement.prepend(defs)
  }

  // Remove existing pattern if already injected (idempotent)
  const existing = defs.querySelector('#treeDStyle-default')
  if (existing) existing.remove()

  const viewBox = (svgElement.getAttribute('viewBox') ?? '').split(/\s+/).map(Number)
  const [, , vbW = 1000, vbH = 1000] = viewBox

  const pattern = document.createElementNS(svgNS, 'pattern')
  pattern.setAttribute('id', 'treeDStyle-default')
  pattern.setAttribute('patternUnits', 'userSpaceOnUse')
  pattern.setAttribute('width', String(vbW))
  pattern.setAttribute('height', String(vbH))

  const image = document.createElementNS(svgNS, 'image')
  image.setAttribute('href', pngUrl)
  // PNG is rendered bottom-up (CAD convention). SVG y-axis goes down.
  // The .apartment_tier group has transform="scale(1,-1)" which flips the
  // coordinate system. The image inside the pattern must be flipped to match:
  //   image.y = -vbH places it above origin, scale(1,-1) flips it down into
  //   the pattern tile (0,0)-(vbW,vbH) in the group's local coordinate space.
  image.setAttribute('x', '0')
  image.setAttribute('y', String(-vbH))
  image.setAttribute('width', String(vbW))
  image.setAttribute('height', String(vbH))
  image.setAttribute('transform', 'scale(1, -1)')

  pattern.appendChild(image)
  defs.appendChild(pattern)
}

/**
 * Injects a <clipPath> definition matching the viewBox to prevent tiling/overflow issues,
 * and applies it directly to the root .apartment_tier <g> element.
 *
 * @param {SVGElement} svgElement
 */
function _injectViewBoxClippingPath(svgElement) {
  const svgNS = 'http://www.w3.org/2000/svg'
  let defs = svgElement.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS(svgNS, 'defs')
    svgElement.prepend(defs)
  }

  // Remove existing clipPath if already injected (idempotent)
  const existingClip = defs.querySelector('#viewbox-clip-default')
  if (existingClip) existingClip.remove()

  const viewBox = (svgElement.getAttribute('viewBox') ?? '').split(/\s+/).map(Number)
  // Fallback to coordinates [0, 0, 1000, 1000] if viewBox parsing fails
  const [vbX = 0, vbY = 0, vbW = 1000, vbH = 1000] = viewBox.length === 4 ? viewBox : [0, 0, 1000, 1000]

  const clipPath = document.createElementNS(svgNS, 'clipPath')
  clipPath.setAttribute('id', 'viewbox-clip-default')

  const clipRect = document.createElementNS(svgNS, 'rect')
  clipRect.setAttribute('x', "0")
  clipRect.setAttribute('y', "0")
  clipRect.setAttribute('width', String(vbW))
  clipRect.setAttribute('height', String(vbH))
  
  clipPath.appendChild(clipRect)
  defs.appendChild(clipPath)

  // Apply the clip-path directly to the main .apartment_tier <g> element to clamp all out-of-bounds rendering
  const apartmentTierGroup = svgElement.querySelector('.apartment_tier')
  if (apartmentTierGroup) {
    apartmentTierGroup.setAttribute('clip-path', 'url(#viewbox-clip-default)')
  }
}


// ── Dimensions ────────────────────────────────────────────────────────────────

/**
 * Reads the SVG viewBox and returns {width, height} of the drawing area.
 * Returns {width:0, height:0} if the attribute is absent or unparseable.
 *
 * @param {SVGElement} svgElement
 * @returns {{ width: number, height: number }}
 */
export function readSvgDimensions(svgElement) {
  const vb = svgElement.getAttribute('viewBox')
  if (!vb) return { width: 0, height: 0 }
  const parts = vb.split(/\s+/).map(Number)
  if (parts.length < 4) return { width: 0, height: 0 }
  return { width: parts[2], height: parts[3] }
}

// ── Main pipeline ─────────────────────────────────────────────────────────────

/**
 * applyStandardSvgModifiers — runs all one-time DOM mutations on a parsed SVGElement.
 *
 * Call this ONCE after DOMParser, before caching. The resulting element is the
 * "canonical base" that all renderers clone from.
 *
 * Mutations applied:
 *   - width/height → 100%
 *   - <use> hrefs → absolute defs.svg URLs
 *   - marker-end/start hrefs → absolute defs.svg URLs
 *   - compartments group z-order → descending
 *   - <pattern id="treeDStyle-default"> → injected into <defs> for 3D variant
 *   - viewBox clipping path → applied to .apartment_tier <g> element
 *
 * Room fills and structural element colours are controlled via .bim-plan CSS
 * custom properties in _plan-base.scss (SSOT). Do NOT inject inline styles or
 * SVG <style> here — that would override the CSS custom property cascade.
 *
 * @param {SVGElement} svgElement — mutated in place
 * @param {string} defsBaseUrl   — from getDefsUrl(projectDataUrl)
 * @param {string} pngUrl        — from getFloorPlanUrl(projectDataUrl, product, refTier, '3d')
 */
export function applyStandardSvgModifiers(svgElement, defsBaseUrl, pngUrl) {
  svgElement.setAttribute('width', '100%')
  svgElement.setAttribute('height', '100%')
  rewriteUseHrefs(svgElement, defsBaseUrl)
  _rewriteMarkerHrefs(svgElement, defsBaseUrl)
  _reorderCompartmentsGroup(svgElement)
  _injectThreeDPatternDef(svgElement, pngUrl)
  _injectViewBoxClippingPath(svgElement)
}
